import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me, signOut, type User } from './lib/auth'
import { t } from './lib/i18n'
import { stash, type Batch, type Pack, type SaleResult, type Summary } from './lib/stash'
import { celebrate, saleKick, sound, buzz, soundOn, setSound } from './lib/fun'
import SignIn from './components/SignIn'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'
import Home from './components/stash/Home'
import Shelf from './components/stash/Shelf'
import Batches from './components/stash/Batches'
import History from './components/stash/History'
import Icon, { type IconName } from './components/Icon'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'home' | 'sell' | 'stash' | 'sales' | 'me'

const TABS: { key: Tab; icon: IconName; label: string }[] = [
  { key: 'home', icon: 'home', label: 'Home' },
  { key: 'sell', icon: 'tag', label: 'Sell' },
  { key: 'stash', icon: 'jar', label: 'Stash' },
  { key: 'sales', icon: 'chart', label: 'Sales' },
  { key: 'me', icon: 'person', label: 'Me' },
]

interface Moment { kind: 'level' | 'badge'; icon: IconName; title: string; sub: string }

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('home')

  const [summary, setSummary] = useState<Summary | null>(null)
  const [batches, setBatches] = useState<Batch[]>([])
  const [packs, setPacks] = useState<Pack[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [moments, setMoments] = useState<Moment[]>([])
  const [sounds, setSounds] = useState(soundOn())

  const refresh = useCallback(async () => {
    setUser(await me())
  }, [])

  const load = useCallback(async () => {
    try {
      const [s, b, p] = await Promise.all([stash.summary(), stash.batches(), stash.packages()])
      setSummary(s)
      setBatches(b)
      setPacks(p)
      setLoadError(null)
    } catch (e) {
      setLoadError((e as Error).message)
    }
  }, [])

  useEffect(() => {
    void (async () => {
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      setReady(true)
    })()
  }, [])

  useEffect(() => { if (user) void load() }, [user, load])

  const onSold = useCallback(async (r: SaleResult) => {
    saleKick(r.soldCents, r.xp)
    setSummary(r.summary)
    const next: Moment[] = []
    if (r.leveledUp) next.push({ kind: 'level', icon: 'bolt', title: `Level ${r.leveledUp.index + 1}`, sub: `You're a ${r.leveledUp.title} now.` })
    for (const b of r.newBadges) next.push({ kind: 'badge', icon: b.icon, title: b.title, sub: b.hint })
    if (next.length) setTimeout(() => setMoments((m) => [...m, ...next]), 900)
    await load()
  }, [load])

  // Each big moment gets its own fanfare as it comes to the front.
  const moment = moments[0]
  useEffect(() => {
    if (!moment) return
    sound(moment.kind === 'level' ? 'level' : 'badge')
    buzz([40, 60, 40])
    celebrate()
  }, [moment])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  const go = (next: Tab) => { setTab(next); scrollTo({ top: 0 }) }
  const ready_ = packs.filter((p) => p.status === 'ready')
  const sold = packs.filter((p) => p.status === 'sold').sort((a, b) => (b.sold_at ?? 0) - (a.sold_at ?? 0))

  return (
    <ErrorBoundary>
      <UpdateBar />
      <EnvBanner health={health} />

      {!user && (
        <main className="signin-wrap">
          <div className="brand-mark">
            <span className="brand-logo"><Icon name="coin" size={56} /></span>
            <h1>Stash</h1>
            <p className="muted">Track it. Bag it. Sell it. Level up.</p>
          </div>
          <SignIn onSignedIn={refresh} />
        </main>
      )}

      {user && (
        <>
          <main className="app-main">
            {loadError && <p className="error">{loadError} <button type="button" className="ghost small" onClick={load}>Retry</button></p>}
            {tab === 'home' && (summary ? <Home s={summary} name={user.displayName} onGo={go} /> : <div className="boot">Loading…</div>)}
            {tab === 'sell' && <Shelf packs={ready_} onSold={onSold} onGo={() => go('stash')} />}
            {tab === 'stash' && <Batches batches={batches} packs={packs} onChange={load} />}
            {tab === 'sales' && <History sold={sold} onChange={load} />}
            {tab === 'me' && (
              <div className="me">
                <h1 className="page-title">Me</h1>
                <div className="card row-between">
                  <span>Sounds</span>
                  <button type="button" className={`toggle ${sounds ? 'on' : ''}`} aria-pressed={sounds} onClick={() => { setSound(!sounds); setSounds(!sounds) }}>
                    <Icon name={sounds ? 'volume' : 'mute'} size={18} /> {sounds ? 'On' : 'Off'}
                  </button>
                </div>
                <Account user={user} />
                {user.capabilities.includes('admin.access') && <Admin />}
                <button type="button" className="ghost wide" onClick={async () => { await signOut(); await refresh() }}>{t('app.signOut')}</button>
                <p className="muted small center">{health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}</p>
              </div>
            )}
          </main>

          <nav className="tabbar" aria-label="Main">
            {TABS.map((x) => (
              <button key={x.key} type="button" aria-current={tab === x.key} onClick={() => go(x.key)}>
                <span className="tab-icon"><Icon name={x.icon} size={24} /></span>
                <span className="tab-label">{x.label}</span>
                {x.key === 'sell' && ready_.length > 0 && <span className="tab-badge">{ready_.length}</span>}
              </button>
            ))}
          </nav>
        </>
      )}

      {moment && (
        <div className="moment-backdrop" onClick={() => setMoments((m) => m.slice(1))}>
          <div className={`moment moment-${moment.kind}`} role="alertdialog" aria-label={moment.title}>
            <div className="moment-icon"><Icon name={moment.icon} size={64} /></div>
            <div className="moment-kicker">{moment.kind === 'level' ? 'LEVEL UP!' : 'BADGE UNLOCKED'}</div>
            <h2>{moment.title}</h2>
            <p>{moment.sub}</p>
            <button type="button" className="cta">Let's go</button>
          </div>
        </div>
      )}

      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}
      <InstallPrompt />
    </ErrorBoundary>
  )
}
